import { useState, useEffect } from 'react'
import PillEditor from 'Shared/components/PillEditor/PillEditor'
import SimpleCharacterCounter from 'Shared/components/SimpleCharacterCounter'
import FileUploader from 'Shared/components/ImageUploader/FileUploader'
import SmsPreview from '../SmsPreview'
import { MAX_WHATSAPP_MESSAGE_LENGTH } from 'Shared/constants'
import './TemplateEditor.scoped.scss'

export default function TemplateEditor({
  template,
  interpolations,
  defaultInterpolations,
  renderedContent,
  templateRenderError,
  mergeTags,
  editing,
  onClearTemplate,
  onUpsertInterpolation,
}) {
  return (
    <div>
      <button className="button navigation" onClick={onClearTemplate}>
        <SvgIconsBack />
        <span>Back to templates</span>
      </button>

      <div className="edit-whatsapp-template">
        <div>
          <div className="whatsapp-template-header">
            <p className="bold no-margin-top no-margin-bottom">
              <span className="ellipsis green"></span>
              {template?.name}
            </p>
            <SimpleCharacterCounter
              content={renderedContent.body}
              max={MAX_WHATSAPP_MESSAGE_LENGTH}
            />
          </div>

          <div className="main">
            <div className="left">
              <div className="inner-box">
                <p
                  className="no-margin-top no-margin-bottom"
                  dangerouslySetInnerHTML={{
                    __html: template?.body?.replace(/\n/g, '<br/>'),
                  }}
                ></p>
              </div>

              {editing && (
                <div className="inner-box">
                  <p className="bold">Content fields</p>
                  <p>Enter text or choose a dynamic tag for each content variable</p>

                  {templateRenderError ? (
                    <p className="error">{templateRenderError}</p>
                  ) : null}

                  <div className="fields">
                    {_.keys(interpolations).map((key, i) => (
                      <InterpolationField
                        key={key}
                        i={i}
                        interpolations={interpolations}
                        defaultInterpolations={defaultInterpolations}
                        template={template}
                        mergeTags={mergeTags}
                        onUpsertInterpolation={onUpsertInterpolation}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="preview">
              <SmsPreview
                name={'Test'}
                content={renderedContent}
                kind="whatsapp"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function InterpolationField({
  i,
  interpolations,
  defaultInterpolations,
  template,
  mergeTags,
  onUpsertInterpolation,
}) {
  const [keyPrefix, setKeyPrefix] = useState(0)
  const [isUploaded, setIsUploaded] = useState(false)

  const key = _.keys(interpolations)[i]
  const value = interpolations[key]

  const isMediaInterpolation = (number) => {
    if (!template?.kind === 'twilio/media') return false

    const url = (template.params.media || []).find((url) => url.includes(`{{${number}}}`))
    return !!url
  }

  const enableUpload = (number) => {
    if (!isMediaInterpolation(number)) return false

    const url = (template.params.media || []).find((url) => url.includes(`{{${number}}}`))
    return url && url.replace(`/{{${number}}}`, '') === window.ENV.CDN_DOMAIN
  }

  function handleFileUploaded([blobId], filename) {
    const uploadUrl = `rails/active_storage/blobs/proxy/${blobId}/${filename}?client_id=${window.ENV.CLIENT_ID}`
    const keys = Object.keys(interpolations)
    const lastKey = keys[keys.length - 1]
    onUpsertInterpolation(lastKey, uploadUrl)
    setKeyPrefix((prev) => prev + 1)
  }

  useEffect(() => {
    if (value?.includes('rails/active_storage/blobs') && enableUpload(i + 1)) {
      setIsUploaded(true)
    } else if (isUploaded) {
      setIsUploaded(false)
    }
  }, [value, i, isUploaded])

  function handleManualEntry() {
    onUpsertInterpolation(key, '')
  }

  return (
    <div className="custom-field" key={i}>
      <div className="field-number">{key}</div>
      <div className="editor-container">
        {!isUploaded && (
          <PillEditor
            condensed
            singleLine={false}
            allowNewLines={true}
            enableEmojis={!isMediaInterpolation(i + 1)}
            initialValue={interpolations[key]}
            onChange={(body) => onUpsertInterpolation(key, body)}
            availableTags={mergeTags}
            key={`${keyPrefix}-${i}`}
            placeholder={defaultInterpolations ? defaultInterpolations[key] : ''}
          />
        )}

        {enableUpload(i + 1) && (
          <div className="margin-top">
            <FileUploader
              existingUrl={interpolations[key]}
              onUploaded={handleFileUploaded}
            />
          </div>
        )}

        {isUploaded && (
          <button className="button naked link" onClick={handleManualEntry}>
            Enter manually
          </button>
        )}
      </div>
    </div>
  )
}
